import React from 'react';
import classes from './SigningChart.module.scss';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import colors from 'scss/variables/colors.module.scss'
import { useTranslation } from 'react-i18next';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAppSelector } from 'store/hook';
import { ISigningItem } from 'interfaces/AnalyticTypes';
import analytics from 'api/analytics';
import { Skeleton } from 'antd';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const colorOfSegments = (isDarkMode: boolean) => ({
  awaiting_signing_1: isDarkMode ? colors.primary100Dark : colors.primary100,
  rejected_signing_1: isDarkMode ? colors.errorDark : colors.error,
  signed_1: isDarkMode ? colors.excel : colors.success,
  
  awaiting_signing_2: isDarkMode ? colors.primary100 : colors.primarytransparent,
  rejected_signing_2: isDarkMode ? colors.error : colors.errorTransparent,
  signed_2: isDarkMode ? colors.success : colors.successTransparent,
});

export const options = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    datalabels: {
      font: {
        size: 14,
      },
      formatter: (value: number) => {
        return value > 0 ? value : '';
      },
    },
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        font: {
          size: 14,
        },
      },
    },
    title: {
      display: true,
      text: 'Chart.js',
      font: {
        size: 14,
      },
    },
  },
};

type SigningChartProps = {
  period: [string, string, string];
  choosenRoom?: string;
}

const SigningChart = ({choosenRoom, period}: SigningChartProps) => {
  const [size, setSize] = React.useState(0);
  const [isLoadingSigning, setIsLoadingSigning] = React.useState(false);
  const [signing, setSigning] = React.useState<ISigningItem | null>(null);

  const wrapElem = React.useRef<HTMLDivElement | null>(null);
  
  const { isDarkMode, messageApi } = useAppSelector((store) => store.windowState);

  const { t } = useTranslation();

  const getAgreements = async (period: [string, string, string], room?: string) => {
    setIsLoadingSigning(true);
    try {
      const agreements = await analytics.getAnalyticsSignatures({room, from: period[0], to: period[1]});  
      setSigning(agreements.data);
    } catch (e) {
      messageApi?.error(t('Analytics.errors.signing'), 6)
    } finally {
      setIsLoadingSigning(false);
    };
  };
  
  React.useEffect(() => {
    getAgreements(period, choosenRoom);
  }, [choosenRoom, period[2]]);

  const updateSize = () => {
    if (Number(wrapElem.current?.offsetWidth) > 500) {
      setSize(500);
    } else {
      setSize(Number(wrapElem.current?.offsetWidth));
    }
  }

  React.useEffect(() => {
    if (wrapElem.current) {
      window.addEventListener('resize', updateSize);
      updateSize();
    }
    return () => window.removeEventListener('resize', updateSize);
  }, [wrapElem.current]);

  const dataArr = Object.values(signing || []);
  const labels = Object.keys(signing || []);
  options.plugins.title.text = t('Analytics.signing.title');

  const themedData = (isDarkMode: boolean) => {
    const currentColors = colorOfSegments(isDarkMode);    

    return {
      labels: labels.map(key => t(`Analytics.signing.${key}`)),
      datasets: [
        {
          datalabels: {
            color: isDarkMode ? colors.activegrey : colors.activegreydark,
          },
          label: t('Analytics.signing.quantity'),
          data: dataArr,
          borderColor: labels.map((key: string) => currentColors[`${key}_1` as keyof typeof currentColors]),
          backgroundColor: labels.map((key: string) => currentColors[`${key}_2` as keyof typeof currentColors]),
          borderRadius: 4,
        },
      ],
    }
  };

  return (
    <div ref={wrapElem} className={classes.signingWrap}>
      {isLoadingSigning
        ? <Skeleton.Avatar style={{marginTop: 20}} active={true} size={size * 0.6} shape='circle' />
        : <Doughnut
          options={options}
          data={themedData(isDarkMode)}
          plugins={[ChartDataLabels]}
        />
      }
    </div>
  );
}

export default SigningChart;
